<main>
  <loading-overlay [isLoading]="isLoading() > 0" [fullWidth]="true"></loading-overlay>
  <p-toast position="bottom-right" key="br"></p-toast>

  <header>
    <div class="headline row-start-center gap-[1rem]">
      <h1>{{ headerTitle() }}</h1>
      @if (entry()) {
        <p-tag severity="warn" [value]="'JournalEntry_Status_Draft' | translateWF: 'Draft'" />
      }
    </div>
    <div class="buttons">
      <span class="separator"></span>
      <p-button [label]="'JournalEntry_Manual_SaveDraft' | translateWF: 'Save draft'" icon="pi pi-save" variant="outlined" (click)="save()" />
      <!-- Posting moves balances and cannot be undone except by a reversing entry, so it stays disabled
           until the entry is one the server would accept. -->
      <p-button [label]="'JournalEntry_Manual_Post' | translateWF: 'Post entry'" icon="pi pi-check" severity="success" [disabled]="!canPost()" (click)="post()" />
      <p-button [label]="'Back' | translateWF: 'Back'" variant="outlined" icon="pi pi-arrow-left" (click)="back()" />
    </div>
  </header>

  <div class="content">
    <form [formGroup]="form" class="col">
      <!-- HEADER — the posting date is the only date a manual entry has, so it is the only one shown. -->
      <p-panel [header]="'JournalEntry_Details' | translateWF: 'Entry details'">
        <p-card>
          <div class="grid-12 gap-[1rem]">
            <div class="span-12-3 col-sm">
              <label
                >{{ 'JournalEntry_PostingDate' | translateWF: 'Posting date' }} <span class="text-red-500">*</span>
                <i
                  class="pi pi-info-circle je-manual__info"
                  [pTooltip]="'JournalEntry_Manual_PostingDate_Tip' | translateWF: 'Decides the fiscal period the entry lands in — that period must be open to post.'"
                  tooltipPosition="top"
                ></i>
              </label>
              <p-datepicker
                formControlName="postingDate"
                [showIcon]="true"
                appendTo="body"
                styleClass="w-full"
                [ngClass]="{ 'ng-invalid ng-dirty': form.controls.postingDate.invalid && form.controls.postingDate.touched }"
              />
            </div>

            <div class="span-12-6 col-sm">
              <label>{{ 'Description' | translateWF: 'Description' }} <span class="text-red-500">*</span></label>
              <input
                pInputText
                formControlName="description"
                class="w-full"
                [placeholder]="'JournalEntry_Manual_Description_Placeholder' | translateWF: 'What this entry records'"
                [ngClass]="{ 'ng-invalid ng-dirty': form.controls.description.invalid && form.controls.description.touched }"
              />
            </div>

            <div class="span-12-3 col-sm">
              <label>{{ 'JournalEntry_Reference' | translateWF: 'Reference' }}</label>
              <input pInputText formControlName="reference" class="w-full" [placeholder]="'JournalEntry_Manual_Reference_Placeholder' | translateWF: 'Optional memo number'" />
            </div>
          </div>

          <!-- Stated rather than offered: a manual entry is captured in dollars, so there is nothing to pick. -->
          <div class="je-manual__fixed">
            <span class="pi pi-dollar"></span>
            <span>
              {{ 'JournalEntry_Manual_CurrencyFixed' | translateWF: 'Manual entries are captured in' }}
              <span class="font-medium">{{ currencyLabel }}</span
              >.
              {{ 'JournalEntry_Manual_CurrencyFixed_Detail' | translateWF: 'What the entry is worth in the books is converted when it posts, at the rate for the posting date.' }}
            </span>
          </div>
        </p-card>
      </p-panel>

      <!-- LINES — account, memo, debit, credit. No site and no cost centre: this screen does not carry them. -->
      <p-panel>
        <ng-template pTemplate="header">
          <div class="row-between-center w-full">
            <span class="font-semibold">{{ 'JournalEntry_Lines' | translateWF: 'Lines' }} <span class="text-red-500">*</span></span>
            <span class="text-sm text-surface-500"> {{ lineItems.length }} {{ 'JournalEntry_LinesLower' | translateWF: 'lines' }} </span>
          </div>
        </ng-template>

        <p-card data-variant="compact">
          <p-table [value]="lineRows()" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '48rem' }">
            <ng-template pTemplate="header">
              <tr>
                <th class="je-manual__line-col">#</th>
                <th>{{ 'JournalEntry_Account' | translateWF: 'Account' }}</th>
                <th>{{ 'Description' | translateWF: 'Description' }}</th>
                <th class="text-right je-manual__amount-col">{{ 'JournalEntry_Debit' | translateWF: 'Debit' }}</th>
                <th class="text-right je-manual__amount-col">{{ 'JournalEntry_Credit' | translateWF: 'Credit' }}</th>
                <th class="je-manual__action-col"></th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
              <tr [formGroup]="getLineGroup(row)">
                <td class="je-manual__line-col">{{ rowIndex + 1 }}</td>

                <td>
                  <!-- Only accounts the General Ledger may post to, and never a control account: a manual line
                     carries no subledger reference, so the server would refuse one at post time. -->
                  <app-financial-account-select
                    [referenceControl]="getLineGroup(row).controls.accountReference"
                    [numberControl]="getLineGroup(row).controls.accountNumber"
                    [nameControl]="getLineGroup(row).controls.accountName"
                    [postingModule]="generalLedgerModule"
                    [excludeSubledgerAccounts]="true"
                    [required]="true"
                    [placeholder]="'JournalEntry_Manual_SelectAccount' | translateWF: 'Select account'"
                  />
                  <!-- Shown the moment the second line picks it, rather than held back until save — by then
                     the user has stopped looking at the row that is wrong. -->
                  @if (getLineGroup(row).hasError('duplicateAccount')) {
                    <small class="je-manual__error">
                      <span class="pi pi-exclamation-circle"></span>
                      {{ 'JournalEntry_Manual_DuplicateAccount' | translateWF: 'Already used on another line — combine them into one amount.' }}
                    </small>
                  }
                </td>

                <td>
                  <input pInputText [formControl]="getLineGroup(row).controls.description" class="w-full" [placeholder]="'Optional' | translateWF: 'Optional'" />
                </td>

                <td class="text-right">
                  <p-inputNumber
                    [formControl]="getLineGroup(row).controls.debitAmount"
                    mode="decimal"
                    [minFractionDigits]="2"
                    [maxFractionDigits]="2"
                    [min]="0"
                    placeholder="0.00"
                    inputStyleClass="text-right w-full"
                    styleClass="w-full"
                  />
                </td>

                <td class="text-right">
                  <p-inputNumber
                    [formControl]="getLineGroup(row).controls.creditAmount"
                    mode="decimal"
                    [minFractionDigits]="2"
                    [maxFractionDigits]="2"
                    [min]="0"
                    placeholder="0.00"
                    inputStyleClass="text-right w-full"
                    styleClass="w-full"
                  />
                </td>

                <td class="je-manual__action-col">
                  @if (canRemoveLine()) {
                    <p-button icon="pi pi-trash" severity="danger" [text]="true" [rounded]="true" [pTooltip]="'Remove' | translateWF: 'Remove'" (click)="removeLine(rowIndex)" />
                  }
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="footer">
              <tr class="je-manual__totals">
                <td colspan="3">{{ 'Total' | translateWF: 'Total' }}</td>
                <td class="text-right tabular-nums">{{ totalDebits() | number: '1.2-2' }} {{ currencyLabel }}</td>
                <td class="text-right tabular-nums">{{ totalCredits() | number: '1.2-2' }} {{ currencyLabel }}</td>
                <td></td>
              </tr>
            </ng-template>
          </p-table>

          <div class="mt-3">
            <p-button [label]="'JournalEntry_Manual_AddLine' | translateWF: 'Add line'" icon="pi pi-plus" size="small" variant="outlined" (click)="addLine()" />
          </div>

          <!-- The same balance strip the details page shows, so a draft reads the way the posted entry will. -->
          <div class="je-manual__balance" [class.je-manual__balance--off]="!isBalanced()">
            <span class="je-manual__balance-state">
              @if (isBalanced()) {
                <span class="pi pi-check-circle"></span>
                {{ 'JournalEntry_Balanced' | translateWF: 'Balanced' }}
              } @else {
                <span class="pi pi-exclamation-triangle"></span>
                {{ 'JournalEntry_Unbalanced' | translateWF: 'Unbalanced' }}
              }
            </span>
            @if (!isBalanced()) {
              <span class="je-manual__muted tabular-nums">
                {{ 'JournalEntry_Manual_OutBy' | translateWF: 'Out by' }}
                {{ difference() | number: '1.2-2' }} {{ currencyLabel }}
              </span>
            }
          </div>
        </p-card>
      </p-panel>
    </form>
  </div>
</main>
